// src/catalog.ts
import {
  PRODUCT_KEY_APPRAISALS,
  PRODUCT_KEY_GUEST_RIDES,
  PRODUCT_KEY_MOBILE_SERVICE,
  PRODUCT_KEY_OPERATIONAL_MOVES,
  PRODUCT_KEY_PARTS_DELIVERY,
  PRODUCT_KEY_VALET_FULL_SERVICE,
  PRODUCT_KEY_VALET_LITE
} from "@hopdrive/sdk-products/catalog";
var ORDER_TYPES = [
  "moves",
  PRODUCT_KEY_GUEST_RIDES,
  PRODUCT_KEY_MOBILE_SERVICE,
  PRODUCT_KEY_PARTS_DELIVERY
];
var ORDER_TYPES_DISPLAY_ORDER = [
  "moves",
  PRODUCT_KEY_MOBILE_SERVICE,
  PRODUCT_KEY_PARTS_DELIVERY,
  PRODUCT_KEY_GUEST_RIDES
];
var PRODUCT_KEYS = [...ORDER_TYPES, PRODUCT_KEY_APPRAISALS];
var VALET_PRODUCT_KEYS = [
  PRODUCT_KEY_VALET_FULL_SERVICE,
  PRODUCT_KEY_VALET_LITE,
  PRODUCT_KEY_OPERATIONAL_MOVES
];
var VALET_KEY_OPERATIONAL_MODE = {
  [PRODUCT_KEY_VALET_LITE]: "self-managed",
  [PRODUCT_KEY_VALET_FULL_SERVICE]: "hopdrive-managed",
  [PRODUCT_KEY_OPERATIONAL_MOVES]: "hopdrive-managed"
};
var PRODUCT_ACCESS_KEYS = {
  moves: VALET_PRODUCT_KEYS,
  [PRODUCT_KEY_GUEST_RIDES]: [PRODUCT_KEY_GUEST_RIDES],
  [PRODUCT_KEY_MOBILE_SERVICE]: [PRODUCT_KEY_MOBILE_SERVICE],
  [PRODUCT_KEY_PARTS_DELIVERY]: [PRODUCT_KEY_PARTS_DELIVERY],
  [PRODUCT_KEY_APPRAISALS]: [PRODUCT_KEY_APPRAISALS]
};
var REQUESTED_PRODUCT_KEYS = [
  ...new Set(PRODUCT_KEYS.flatMap((key) => PRODUCT_ACCESS_KEYS[key]))
];

// src/assembleLocalInputs.ts
function probe(rows, timestampField) {
  const row = rows[0];
  if (!row) return { hasOrders: false, lastOrderAt: null };
  const at = row[timestampField];
  return { hasOrders: true, lastOrderAt: typeof at === "string" ? at : null };
}
function assembleHistory(data) {
  return {
    moves: probe(data.moves, "pickup_time"),
    "guest-rides": probe(data.guestRides, "created_at"),
    "mobile-service": probe(data.mobileService, "departure_time"),
    "parts-delivery": probe(data.partsDelivery, "departure_time"),
    appraisals: probe(data.appraisals, "last_save_time_utc")
  };
}
function assembleLocalInputs(data, customerId) {
  return {
    customerId,
    config: data.customers_by_pk?.effective_config ?? {},
    history: assembleHistory(data)
  };
}
function emptyLocalProductData(customerId) {
  const history = Object.fromEntries(
    PRODUCT_KEYS.map((key) => [key, { hasOrders: false, lastOrderAt: null }])
  );
  return { customerId, config: {}, history };
}
function withEmptyOnFailure(local, customerId) {
  return local.catch((err) => {
    console.error("Failed to load the portal product inputs; degrading presentation", err);
    return emptyLocalProductData(customerId);
  });
}

// src/ui/productCopy.ts
import {
  PRODUCT_KEY_APPRAISALS as PRODUCT_KEY_APPRAISALS2,
  PRODUCT_KEY_GUEST_RIDES as PRODUCT_KEY_GUEST_RIDES2,
  PRODUCT_KEY_MOBILE_SERVICE as PRODUCT_KEY_MOBILE_SERVICE2,
  PRODUCT_KEY_PARTS_DELIVERY as PRODUCT_KEY_PARTS_DELIVERY2,
  productLabel
} from "@hopdrive/sdk-products/catalog";
var PRODUCT_LABELS = {
  moves: "Valet",
  [PRODUCT_KEY_GUEST_RIDES2]: productLabel(PRODUCT_KEY_GUEST_RIDES2),
  [PRODUCT_KEY_MOBILE_SERVICE2]: productLabel(PRODUCT_KEY_MOBILE_SERVICE2),
  [PRODUCT_KEY_PARTS_DELIVERY2]: productLabel(PRODUCT_KEY_PARTS_DELIVERY2),
  [PRODUCT_KEY_APPRAISALS2]: productLabel(PRODUCT_KEY_APPRAISALS2)
};
function resolveMovesLabel(heldModes) {
  return heldModes?.hasOperationalMoves ? "Moves" : PRODUCT_LABELS.moves;
}
function prettifyKey(key) {
  const words = key.replace(/([a-z])([A-Z])/g, "$1 $2").split(/[_\s]+/).filter(Boolean);
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
var REASON_COPY = {
  not_entitled: "This product is not part of your current agreement",
  contract_expired: "Your contract for this product has expired",
  disabled_by_config: "This product is turned off for your rooftop",
  credit_limit_exceeded: "Your outstanding balance has reached its limit, so new orders are paused",
  payment_required: "A payment is required before new orders",
  payment_failed: "Your latest payment did not go through",
  missing_payment_method: "No payment method is on file",
  administratively_suspended: "This product is temporarily suspended",
  history_window: "Your history stays viewable while your enrollment is inactive"
};
function reasonText(reason) {
  if (!reason) return null;
  return REASON_COPY[reason.key] ?? prettifyKey(reason.key);
}
function actionLabel(type) {
  const labels = {
    pay_now: "Pay now",
    update_payment_method: "Update payment method",
    contact_sales: "Contact sales",
    request_access: "Request access"
  };
  return labels[type] ?? prettifyKey(type);
}
var SALES_EMAIL = "sales@hopdrive.com";
function salesMailto(subject = "Product inquiry") {
  return `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(subject)}`;
}
var SALES_MAILTO = salesMailto();

export {
  ORDER_TYPES,
  ORDER_TYPES_DISPLAY_ORDER,
  PRODUCT_KEYS,
  VALET_PRODUCT_KEYS,
  VALET_KEY_OPERATIONAL_MODE,
  PRODUCT_ACCESS_KEYS,
  REQUESTED_PRODUCT_KEYS,
  assembleLocalInputs,
  emptyLocalProductData,
  withEmptyOnFailure,
  PRODUCT_LABELS,
  resolveMovesLabel,
  prettifyKey,
  reasonText,
  actionLabel,
  SALES_EMAIL,
  salesMailto,
  SALES_MAILTO
};
//# sourceMappingURL=chunk-S6FSOA4D.mjs.map